// MissionsIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/MissionsIndex.css";
import ToolbarHeader from "./components/ToolbarHeader";
import MissionForm from "../Profile/MissionForm"; // adjust path if needed
import Modal from "./components/Modal";
export default function MissionsIndex({ user }) {
// 1) state
const [missions, setMissions] = useState([]);
const [search, setSearch] = useState("");
const [loading, setLoading] = useState(true);
const [showModal, setShowModal] = useState(false);
const [filter, setFilter] = useState("all");
const [commentCounts, setCommentCounts] = useState({});

// 2) helpers (keep here)
const norm = (s) => (s || "").toString().trim().toLowerCase();
const isMain = (m) => ["main", "main story", "story", "campaign"].includes(norm(m.category));
const isSide = (m) => ["side", "side mission", "secondary"].includes(norm(m.category));
const isCustom = (m) => ["custom", "fan"].includes(norm(m.category)) || !!m.fanCreated;
const isMine = (m, user) => {
  const uid = user?.uid;
  return !!uid && [m.createdBy, m.userId, m.authorId, m.ownerId].some((x) => x === uid);
};

// 3) list memo (👈 must be before any effect that uses `list`)
const list = useMemo(() => {
  let arr = missions.filter((m) => !m.isPrivate || isMine(m, user)); // visibility gate

  if (filter !== "all") {
    arr = arr.filter((m) => {
      if (filter === "main") return isMain(m);
      if (filter === "side") return isSide(m);
      if (filter === "custom") return isCustom(m);
      if (filter === "mine") return isMine(m, user);
      return true;
    });
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    arr = arr.filter((m) =>
      [m.missionName, m.location, ...(m.tags || []), m.category]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }

  arr.sort((a, b) => {
    const an = a.missionNumber ?? Infinity;
    const bn = b.missionNumber ?? Infinity;
    if (an !== bn) return an - bn;
    return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
  });

  return arr;
}, [missions, search, filter, user]);

// 4) effects can now use `list` safely
useEffect(() => {
  if (!list.length) {
    setCommentCounts({});
    return;
  }
  let cancelled = false;
  (async () => {
    const results = await Promise.all(
      list.map(async (m) => {
        try {
          const q = query(
            collection(db, "missions", m.id, "threads"),  // ⬅️ subcollection, not root
            where("type", "==", "comment")
          );
          const snap = await getCountFromServer(q);
          return [m.id, snap.data().count];
        } catch (e) {
          console.warn("count() failed for", m.id, e);
          return [m.id, undefined];
        }

      })
    );
    if (!cancelled) setCommentCounts(Object.fromEntries(results));
  })();
  return () => {
    cancelled = true;
  };
}, [list]);


  // Esc to close + scroll lock
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => e.key === "Escape" && setShowModal(false);
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showModal]);

  // Load missions
  useEffect(() => {
    // helper to merge snapshots by id
    const mergeById = (...lists) => {
      const map = new Map();
      lists.flat().forEach((m) => map.set(m.id, m));
      // sort by createdAt desc (fallback 0)
      return [...map.values()].sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    };

    setLoading(true);

    // 1) Public missions
    const qPublic = query(
      collection(db, "missions"),
      where("isPrivate", "==", false),
      orderBy("createdAt", "desc")
    );

    let publicDocs = [];
    let mineDocs = [];
    const unsubs = [];

    const unsubPublic = onSnapshot(qPublic, (snap) => {
      publicDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMissions((prev) => mergeById(publicDocs, mineDocs));
      setLoading(false);
    });
    unsubs.push(unsubPublic);

    // 2) My missions (may include private). Pick ONE authoritative owner field.
    // If your schema varies, prefer normalizing to `ownerId` on write.
    if (user?.uid) {
      // If you *must* support legacy fields, you can add extra listeners:
      // ownerId==uid OR createdBy==uid (FireStore doesn't support OR on fields)
      // so we listen to both and merge.
      const qs = [
        query(
          collection(db, "missions"),
          where("ownerId", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
        query(
          collection(db, "missions"),
          where("createdBy", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
      ];

      qs.forEach((qq) => {
        const u = onSnapshot(qq, (snap) => {
          // merge *these* "mine" results together first
          const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // merge into mineDocs by id (so ownerId & createdBy sets don’t duplicate)
          const tmp = new Map(mineDocs.map((m) => [m.id, m]));
          batch.forEach((m) => tmp.set(m.id, m));
          mineDocs = [...tmp.values()];

          // now merge with public and push to state
          setMissions(mergeById(publicDocs, mineDocs));
          setLoading(false);
        });
        unsubs.push(u);
      });
    }

    return () => unsubs.forEach((u) => u && u());
  }, [db, user?.uid]);


  return (
    <>
      <div className="msn-wrap">
        <ToolbarHeader
          search={search}
          setSearch={setSearch}
          placeholder="Search missions, tags, location…"
          onAddMission={() => setShowModal(true)}
          user={user}
          addLabel="＋ Add Mission"
        >
          <div className="forum-tabs">
            {["all", "main", "side", "custom"]
              .concat(user ? ["mine"] : [])
              .map((t) => (
                <span
                  key={t}
                  className={`tab-text ${filter === t ? "active" : ""}`}
                  onClick={() => setFilter(t)}
                >
                  {t === "all"
                    ? "All"
                    : t === "main"
                    ? "Main Story"
                    : t === "side"
                    ? "Side Missions"
                    : t === "custom"
                    ? "Custom Missions"
                    : "My Missions"}
                </span>
              ))}
          </div>
        </ToolbarHeader>

        <ul className="msn-layout">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <li key={`sk-${i}`} className="msn-card msn-card--skeleton">
                <div className="msn-thumb skeleton" />
                <div className="msn-card-body">
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line short" />
                </div>
              </li>
            ))}

          {!loading &&
            list.map((m) => (
              // ...inside the map(m => ...) render

              <li key={m.id} className="msn-card" tabIndex={0}>
                <Link
                  to={`/community/missions/${m.id}`}
                  state={{ user }}
                  className="msn-card-link"
                >
                  {/* Thumbnail */}
                  <div className="msn-thumb">
                    {m.coverImage ? (
                      <img
                        className="msn-img"
                        src={m.coverImage}
                        alt={m.missionName}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="msn-img msn-img--placeholder">🎯</div>
                    )}

                    {/* TOP ROW: pills (Category, Game, Owner) */}
                    <div className="msn-toprow">
                      {/* Category pill */}
                      <span className="msn-pill msn-pill--cat">
                        {m.category || "Mission"}
                      </span>

                      {/* Game pill (supports several common field names) */}
                      {(m.game || m.gameTitle || m.gameName) && (
                        <span className="msn-pill msn-pill--game" title="Game">
                          🎮 {m.game || m.gameTitle || m.gameName}
                        </span>
                      )}

                      {/* Owner crown pill if this is the user's mission; else show Fan if applicable */}
                      {isMine(m, user) ? (
                        <span
                          className="msn-pill msn-pill--owner"
                          title="You created this"
                        >
                          👑 Owner
                        </span>
                      ) : (
                        m.fanCreated && <span className="msn-pill">Fan</span>
                      )}
                    </div>

                    {/* TITLE BAR: # + lock/public before mission name */}
                    <div className="msn-titlebar">
                      <h3 className="msn-name">
                        <span className="msn-prefix">
                          {m.missionNumber != null && (
                            <span className="msn-num">#{m.missionNumber} {m.missionName}</span>
                          )}
                          
                          <span
                            className={`msn-chip ${
                              m.isPrivate
                                ? "msn-chip--private"
                                : "msn-chip--public"
                            }`}
                            title={
                              m.isPrivate ? "Private mission" : "Public mission"
                            }
                          >
                            {m.isPrivate ? "Private 🔒" : "Public 🌐"}
                          </span>
                        </span>
                        
                      </h3>
                    </div>
                  </div>

                  {/* Body (unchanged except we removed the old top badges) */}
                  <div className="msn-card-body">
                    <div className="msn-meta">
                      {m.location && <span>{m.location}</span>}
                      {m.difficulty && <span> • {m.difficulty}</span>}
                      {m.estDurationMins && (
                        <span> • ~{m.estDurationMins}m</span>
                      )}
                    </div>

                    <div className="msn-stats" aria-label="Mission stats">
                      <div className="msn-stat" title="Comments">
                        <span className="msn-stat-icn" aria-hidden>
                          💬
                        </span>
                        <span className="msn-stat-num">
                          {m.commentCount ?? 0}
                        </span>
                      </div>
                      <div className="msn-stat" title="Reactions">
                        <span className="msn-stat-icn" aria-hidden>
                          👍
                        </span>
                        <span className="msn-stat-num">
                          {m.likeCount ?? 0}
                        </span>
                      </div>
                      <div className="msn-stat" title="Completed by players">
                        <span className="msn-stat-icn" aria-hidden>
                          🏁
                        </span>
                        <span className="msn-stat-num">
                          {m.completedCount ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}

          {!loading && list.length === 0 && (
            <div className="msn-empty">No missions match your search.</div>
          )}
        </ul>
      </div>

      {/* Reusable Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Submit a Mission"
        size="md" // "sm" | "md" | "lg" | number (percentage)
        closeOnBackdrop={true}
        showClose={true}
      >
        <MissionForm
          user={user}
          enforceAdmin={false}
          isAdmin={false}
          extraMeta={{ status: "pending", source: "user" }}
          onSaved={() => setShowModal(false)}
          onCancel={() => setShowModal(false)}
          initial={{ difficulty: "Normal" }}
          mode="create"
        />
      </Modal>
    </>
  );
}
